import {
  EmptyData, SecondaryButton,
  Flex, Button, FlexColumn, LinkButton, P,
  TextareaInput,
  SelectInput,
  useStyledTheme,
  Icon,
  defaultGuid,
} from '@equifinance/shared';
import { formatUserName, formatDateTime, convertToNumber } from '@equifinance/shared/utils';
import styled, { css } from 'styled-components';
import { useState, useRef, useEffect } from 'react';
import { Note, NoteType, useTasksContext } from '../../../contexts/tasks';
import { usePageConfigurationContext } from '../../../contexts/pageConfiguration';

const NotesContainer = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[4]};
  width: 100%;
`}`;

const NoteListContainer = styled.div``;
const ComposeNoteContainer = styled(FlexColumn) <{ boxShadow?: boolean }>`${({ theme, boxShadow }) => `
  padding: 0 ${theme.padding[4]};
  gap: ${theme.padding[4]};
  ${boxShadow && 'box-shadow: 0 -1px 2px 0 rgba(16, 24, 40, 0.10);'}
`}`;

const NoteList = styled(FlexColumn) <{ showAll?: boolean }>`${({ theme, showAll }) => `
  gap: ${theme.padding[2]};
  margin: 0 ${theme.padding[4]};
  overflow-y: auto;
  ${!showAll && `max-height: ${theme.maxNotesSectionHeight};`}
`}`;

const NoteItem = styled(Flex)``;

const NoteInfo = styled(FlexColumn) <{ isExternalUser?: boolean }>`${({ theme, isExternalUser }) => `
  gap: ${theme.padding[4]};
  align-items: flex-start;
  justify-content: space-between;
  width: ${theme.buttonSubmitWidth};
  padding: ${theme.padding[3]} ${theme.padding[4]};
  background: ${isExternalUser ? theme.colours.neutral[8] : theme.colours.primary[10]};
  border-top-left-radius: ${theme.borderRadius[2]};
  border-bottom-left-radius: ${theme.borderRadius[2]};
  font-family: ${theme.fontFamily[1]};
  font-weight: ${theme.fontWeight.normal};
`}`;

const NoteCreator = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
`}`;

const NoteTextSymbol = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const NoteDateTime = styled(P)`${({ theme }) => `
  color: ${theme.colours.neutral[3]}; 
`}`;

const NoteContent = styled(P) <{ isExternalUser?: boolean }>`${({ theme, isExternalUser }) => `
  padding: ${theme.padding[3]} ${theme.padding[4]};
  flex: 1;
  background: ${isExternalUser ? theme.colours.neutral[9] : theme.colours.primary[11]};
  border-top-right-radius: ${theme.borderRadius[2]};
  border-bottom-right-radius: ${theme.borderRadius[2]};
  color: ${theme.colours.neutral[2]};
  white-space: pre-wrap;
`}`;

const ViewAllContainer = styled.div`${({ theme }) => `
  position: relative;
  box-shadow: 0 -1px 2px 0 rgba(16, 24, 40, 0.10);
  border-bottom: ${theme.borderWidth[1]} solid ${theme.colours.primary[3]};
`}`;

const ViewAllBtn = styled(LinkButton)`${({ theme }) => `
  height: ${theme.smallButtonHeight};
  width: 100%;
  padding: 0;
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[2]};
  font-weight: ${theme.fontWeight.bold};
  border-radius: 0;

  &:focus {
    border: none;
    box-shadow: none;
    outline: none;
  };

  &:hover {
    background: ${theme.colours.primary[8]};
  };
`}`;

const EmptyNoteContainer = styled(FlexColumn)`${({ theme }) => `
  padding: 0 ${theme.padding[4]};
`}`;

const ActionBtnContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[4]};
  justify-content: flex-end;
`}`;

const BtnContainer = styled(Flex) <{ hasNote?: boolean }>`${({ theme, hasNote }) => `
  justify-content: ${hasNote ? 'flex-end' : 'center'};
  margin-top: -${hasNote ? 0 : theme.padding[4]};
`}`;

const noteBtnStyles = css`${({ theme }) => `
  height: ${theme.smallButtonHeight};
  min-width: ${theme.commisionInputWidth};
  padding: ${theme.padding[2]} ${theme.padding[5]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bolder};
  line-height: ${theme.fontSize[3]};
`}`;

const NoteCancelBtn = styled(SecondaryButton)`
  ${noteBtnStyles}
`;

const NoteStartBtn = styled(SecondaryButton)`
  ${noteBtnStyles}
`;

const NoteSubmitBtn = styled(Button)`
  ${noteBtnStyles}
`;

const TypeSelectInput = styled.div`${({ theme }) => `
  width: ${theme.dropdownWidth};
`}`;

const CallIcon = styled(Icon).attrs(() => ({
  className: 'mi-call',
}))` ${({ theme }) => `
  color:${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[5]};
`}`;

const EmailIcon = styled(Icon).attrs(() => ({
  className: 'mi-email',
}))` ${({ theme }) => `
  color:${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[5]};
`}`;

enum NoteTypeOptions {
  Call = 'Call',
  Email = 'Email',
  General = 'General',
}

function NoteSymbol({ type }: { type: string }) {
  switch (type) {
    case 'Call': return <CallIcon />;
    case 'Email': return <EmailIcon />;
    case 'General': return <NoteTextSymbol>General</NoteTextSymbol>;
    case 'SystemNote': return <NoteTextSymbol>System</NoteTextSymbol>;
    default: return <></>;
  }
}

type NotesProps = {
  taskId: string,
  notes: Note[],
  isExternalNote?: boolean,
  canAddNote?: boolean,
}

export function Notes({
  taskId, notes = [], isExternalNote = true, canAddNote = true,
}: NotesProps) {
  const theme = useStyledTheme();
  const [showAll, setShowAll] = useState(false);
  const [displayAllBtn, setDisplayAllBtn] = useState(false);
  const [creatingNote, setCreatingNote] = useState(false);
  const [note, setNote] = useState('');
  const [type, setType] = useState('');
  const notesListRef = useRef<HTMLDivElement>(null);
  const maxNotesHeight = Number(convertToNumber(theme.maxNotesSectionHeight));
  const { postNote } = useTasksContext();
  const { isReadOnly } = usePageConfigurationContext();

  const toggleViewAll = () => {
    setShowAll((showAll) => !showAll);
  };

  useEffect(() => {
    const checkNotesHeight = () => {
      if (notesListRef?.current) {
        const rect = notesListRef?.current.getBoundingClientRect();
        setDisplayAllBtn(Math.round(rect.height) >= maxNotesHeight);
      }
    };

    const timeoutId = setTimeout(checkNotesHeight, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [notes]);

  const onCreatingNote = () => {
    setType('');
    setNote('');
    setCreatingNote(true);
  };

  const onCancelNote = () => {
    setType('');
    setNote('');
    setCreatingNote(false);
  };

  const onNoteChange = (value: string) => {
    setNote(value);
  };

  const onTypeChange = (value: string) => {
    setType(value);
  };

  const onCreateNote = async () => {
    setNote('');
    setType('');
    setCreatingNote(false);
    await postNote({
      taskId,
      note,
      type: isExternalNote ? NoteType.General : type as Exclude<NoteType, NoteType.SystemNote>,
      privateFlag: !isExternalNote,
    });
  };

  return (
    <NotesContainer>
      {notes.length > 0 && (
        <NoteListContainer>
          <NoteList ref={notesListRef} showAll={showAll}>
            {notes.map((note) => (
              <NoteItem key={note.id}>
                <NoteInfo isExternalUser={defaultGuid !== note.noteFromCompanyId}>
                  {isExternalNote ? <NoteTextSymbol>{note.noteFromCompanyName}</NoteTextSymbol> : <NoteSymbol type={note.type} />}
                  <FlexColumn>
                    {note.type !== NoteType.SystemNote && <NoteCreator>{formatUserName(note.noteFromUserName)}</NoteCreator>}
                    <NoteDateTime>{formatDateTime(note.moment, 'DD/MM/YYYY, HH:mm:ss')}</NoteDateTime>
                  </FlexColumn>
                </NoteInfo>
                <NoteContent isExternalUser={defaultGuid !== note.noteFromCompanyId}>
                  {note?.note}
                </NoteContent>
              </NoteItem>
            ))}
          </NoteList>
          {displayAllBtn && (
            <ViewAllContainer>
              <ViewAllBtn onClick={toggleViewAll}>
                {showAll ? 'Show less' : `View all (${notes.length})`}
              </ViewAllBtn>
            </ViewAllContainer>
          )}
        </NoteListContainer>
      )}

      {creatingNote
        ? (
          <ComposeNoteContainer boxShadow={!displayAllBtn && !!notes?.length}>
            {!isExternalNote
              && (
                <TypeSelectInput>
                  <SelectInput
                    id="note-type"
                    label="Type"
                    placeholder="Select"
                    onChange={onTypeChange}
                    options={NoteTypeOptions}
                    value={type}
                    required
                  />
                </TypeSelectInput>
              )}
            <TextareaInput
              id="note-content"
              label="Note"
              required
              rows={3}
              maxHeight={1000}
              value={note}
              onChange={onNoteChange}
            />
            <ActionBtnContainer>
              {!isExternalNote && <NoteCancelBtn onClick={onCancelNote}>Cancel</NoteCancelBtn>}
              <NoteSubmitBtn disabled={(!isExternalNote && !type) || !note} onClick={onCreateNote}>{isExternalNote ? 'Post note' : 'Post'}</NoteSubmitBtn>
            </ActionBtnContainer>
          </ComposeNoteContainer>
        )
        : (
          <EmptyNoteContainer>
            {notes.length === 0 && <EmptyData title="No notes found." message="" className="mi-message" />}
            <BtnContainer hasNote={!!notes.length}>
              <NoteStartBtn onClick={onCreatingNote} disabled={isReadOnly || !canAddNote}>Start a note</NoteStartBtn>
            </BtnContainer>
          </EmptyNoteContainer>
        )}
    </NotesContainer>
  );
}
